import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus } from "./types";

const BASE_URL = import.meta.env.REACT_APP_API_ENDPOINT + "/company/calculation-base-labels";

/**
 * title_channel_sales column that can be selected as a contract's calculation base.
 * Values mirror the backend `CalculationBaseColumn` enum.
 */
export type CalculationBaseColumn = "quantity" | "turnover" | "turnover_1" | "turnover_2";

export const CALCULATION_BASE_COLUMNS: CalculationBaseColumn[] = ["quantity", "turnover", "turnover_1", "turnover_2"];

export interface ICalculationBaseColumnLabel {
    id: number;
    calculationBaseColumn: CalculationBaseColumn;
    label: string;
    createdAt: string;
    updatedAt: string;
}

export const getCalculationBaseColumnLabels = createAsyncThunk(
    "calculationBaseColumnLabels/getAll",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(BASE_URL);
            return response.data.data as ICalculationBaseColumnLabel[];
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const upsertCalculationBaseColumnLabel = createAsyncThunk(
    "calculationBaseColumnLabels/upsert",
    async (
        {
            calculationBaseColumn,
            label,
        }: { calculationBaseColumn: CalculationBaseColumn; label: string; silent?: boolean },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(`${BASE_URL}/${calculationBaseColumn}`, { label });
            return response.data.data as { calculationBaseColumn: CalculationBaseColumn; label: string };
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const deleteCalculationBaseColumnLabel = createAsyncThunk(
    "calculationBaseColumnLabels/delete",
    async (
        { calculationBaseColumn }: { calculationBaseColumn: CalculationBaseColumn; silent?: boolean },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.delete(`${BASE_URL}/${calculationBaseColumn}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

interface InitialState {
    labels: ICalculationBaseColumnLabel[];
    labelsStatus: TFetchStatus;
}

const initialState: InitialState = {
    labels: [],
    labelsStatus: "idle",
};

const calculationBaseColumnLabelsStore = createSlice({
    name: "calculationBaseColumnLabels",
    initialState,
    reducers: {},
    extraReducers(builders) {
        builders.addCase(getCalculationBaseColumnLabels.pending, (state) => {
            state.labelsStatus = "pending";
        });
        builders.addCase(getCalculationBaseColumnLabels.fulfilled, (state, { payload }) => {
            state.labels = payload;
            state.labelsStatus = "succeeded";
        });
        builders.addCase(getCalculationBaseColumnLabels.rejected, (state) => {
            state.labelsStatus = "failed";
        });
        builders.addCase(upsertCalculationBaseColumnLabel.fulfilled, (state, action) => {
            const { payload } = action;
            const existing = state.labels.find((item) => item.calculationBaseColumn === payload.calculationBaseColumn);
            if (existing) {
                existing.label = payload.label;
            } else {
                state.labels = [
                    ...state.labels,
                    {
                        id: Date.now(),
                        calculationBaseColumn: payload.calculationBaseColumn,
                        label: payload.label,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    },
                ];
            }
            if (!action.meta.arg.silent) {
                toast.success(`${t("CalculationBaseColumnLabels.Store.SaveSuccess")}`);
            }
        });
        builders.addCase(upsertCalculationBaseColumnLabel.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builders.addCase(deleteCalculationBaseColumnLabel.fulfilled, (state, action) => {
            state.labels = state.labels.filter(
                (item) => item.calculationBaseColumn !== action.meta.arg.calculationBaseColumn,
            );
            if (!action.meta.arg.silent) {
                toast.success(`${t("CalculationBaseColumnLabels.Store.DeleteSuccess")}`);
            }
        });
        builders.addCase(deleteCalculationBaseColumnLabel.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});

export default calculationBaseColumnLabelsStore.reducer;
